import {
  BarChart2,
  FileText,
  LayoutDashboard,
  User as LucideUser,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { logout } from "@/redux/auth/authSlice";
import { RootState } from "@/store";

interface DriverLayoutProps {
  children?: React.ReactNode;
}

const DriverLayout: React.FC<DriverLayoutProps> = ({ children }) => {
  // Only allow driver roles to access this layout
  const driverRoles = ["DeliveryDriver", "ScooterDriver", "VehicleDriver"];
  const businessRoles = ["CourierOwner", "FleetOwner", "RestaurantOwner"];
  const adminRoles = ["admin", "manager", "staff"];

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage or system preference
    const saved = localStorage.getItem("theme");
    if (saved) return saved === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  React.useEffect(() => {
    if (user) {
      if (!driverRoles.includes(user.role)) {
        if (businessRoles.includes(user.role)) {
          navigate("/maintenance", { replace: true });
        } else if (adminRoles.includes(user.role)) {
          navigate("/admin/dashboard", { replace: true });
        } else {
          navigate("/auth/login", { replace: true });
        }
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setProfileDropdownOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownOpen]);

  // Theme toggle functionality
  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const navigation = [
    {
      href: "/driver/dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
      name: "Dashboard",
    },
    {
      href: "/driver/documents",
      icon: <FileText className="h-5 w-5" />,
      name: "Documents",
    },
    {
      href: "/driver/analytics",
      icon: <BarChart2 className="h-5 w-5" />,
      name: "Analytics",
    },
    {
      href: "/driver/profile",
      icon: <LucideUser className="h-5 w-5" />,
      name: "Profile",
    },
  ];

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    (async () => {
      await import("@/services/authService").then(({ authService }) =>
        authService.logout(),
      );
      dispatch(logout());
      navigate("/auth/login");
      setShowLogoutConfirm(false);
    })();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const isCurrentPath = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`${
          isMobile
            ? `fixed inset-y-0 left-0 z-50 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out dark:bg-gray-800 ${
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
              }`
            : "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg dark:bg-gray-800"
        }`}
        style={
          !isMobile ? { position: "fixed", transform: "translateX(0px)" } : {}
        }
      >
        <div className="flex h-16 items-center justify-between bg-blue-600 px-4 dark:bg-blue-700 md:px-6">
          <Link className="flex items-center" to="/">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white dark:bg-gray-800 md:h-10 md:w-10">
              <svg
                className="h-5 w-5 text-blue-600 dark:text-blue-400 md:h-6 md:w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
            <span className="ml-2 text-xl font-bold text-white dark:text-gray-100 md:text-2xl">
              Hareeng
            </span>
          </Link>
          {isMobile && (
            <button
              className="text-white hover:text-gray-200"
              onClick={() => setSidebarOpen(false)}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M6 18L18 6M6 6l12 12"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="mt-8">
          <div className="px-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Driver Portal
            </p>
          </div>
          {navigation.map((item) => (
            <Link
              className={`flex items-center px-4 py-3 text-sm font-medium transition-colors duration-150 md:px-6 md:py-4 md:text-base ${
                isCurrentPath(item.href)
                  ? "border-r-3 border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                  : "text-gray-700 hover:bg-gray-50 hover:text-blue-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-blue-400"
              }`}
              key={item.name}
              onClick={() => setSidebarOpen(false)}
              to={item.href}
            >
              <span
                className={`mr-3 md:mr-4 ${isCurrentPath(item.href) ? "text-blue-600 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"}`}
              >
                {item.icon}
              </span>
              {item.name}
            </Link>
          ))}
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-4 dark:border-gray-700 md:p-6">
          <div className="flex items-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 md:h-12 md:w-12">
              <LucideUser className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3 md:ml-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 md:text-base">
                {user?.firstName
                  ? `${user.firstName} ${user.lastName}`.trim()
                  : "Driver"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 md:text-sm">
                {user?.role || "VehicleDriver"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div
        className={isMobile ? "" : "pl-64"}
        style={!isMobile ? { paddingLeft: "256px" } : {}}
      >
        {/* Top navbar */}
        <div className="sticky top-0 z-30 border-b border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 md:px-8">
            {/* Mobile menu button */}
            {isMobile && (
              <button
                className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-400"
                onClick={() => setSidebarOpen(true)}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </button>
            )}

            {/* Page title */}
            <div className="min-w-0 flex-1 md:ml-0">
              <h1 className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100 md:text-xl">
                {navigation.find((item) => isCurrentPath(item.href))?.name ||
                  "Driver Dashboard"}
              </h1>
            </div>

            {/* Right side - Theme toggle and Profile dropdown */}
            <div className="flex items-center space-x-3">
              {/* Theme Toggle */}
              <button
                className="flex items-center justify-center rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                onClick={toggleTheme}
                title={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              >
                {isDarkMode ? (
                  // Sun icon for light mode
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                ) : (
                  // Moon icon for dark mode
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                )}
              </button>

              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                {/* Debug indicator */}
                {profileDropdownOpen && (
                  <div className="absolute -right-2 -top-2 h-2 w-2 rounded-full bg-red-500"></div>
                )}
                <button
                  className="flex cursor-pointer items-center rounded-full p-2 text-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:hover:bg-gray-700"
                  onClick={() => {
                    setProfileDropdownOpen(!profileDropdownOpen);
                  }}
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                    <LucideUser className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <svg
                    className="ml-1 h-4 w-4 text-gray-400 dark:text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M19 9l-7 7-7-7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                    />
                  </svg>
                </button>

                {/* Profile dropdown */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 z-[60] mt-2 min-w-48 max-w-xs rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:ring-gray-600 dark:ring-opacity-25">
                    <div className="py-1">
                      <div className="border-b border-gray-100 px-4 py-2 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                        Signed in as <br />
                        <span className="break-words font-medium text-gray-900 dark:text-gray-100">
                          {user?.email}
                        </span>
                      </div>
                      <Link
                        className="block cursor-pointer px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProfileDropdownOpen(false);
                        }}
                        to="/driver/profile"
                      >
                        Profile Settings
                      </Link>
                      <button
                        className="block w-full cursor-pointer px-4 py-2 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setProfileDropdownOpen(false);
                          handleLogout();
                        }}
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">{children || <Outlet />}</main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={cancelLogout}
          ></div>

          {/* Modal */}
          <div className="relative z-[101] w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all dark:bg-gray-800">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>

            <div className="text-center">
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                Confirm Logout
              </h3>
              <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to log out? You'll need to sign in again
                to access your account.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                onClick={cancelLogout}
              >
                Cancel
              </button>
              <button
                className="flex-1 rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                onClick={confirmLogout}
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverLayout;
